import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import DateTimeUtils from '@/utils/DateTimeUtils';
import TimeoutWorker from '@/utils/TimeoutWorker';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import AuthTokenManager from '@/manager/AuthTokenManager';

// Refresh this number of seconds prior to expiration.
const SecondsBuffer = 30;

/**
 * Handles the periodic renewing of the QuickSource auth token using a
 * TimeoutWorker and the current token's expiration date/time.
 */
export default class AuthTokenHandler {
    private timeoutWorker: TimeoutWorker;

    /**
     * Handles the periodic renewing of the QuickSource auth token using a
     * TimeoutWorker and the current token's expiration date/time.
     */
    constructor() {
        this.timeoutWorker = new TimeoutWorker();
    }

    public async start(): Promise<void> {
        this.timeoutWorker.setCallback(this.refreshAndRestart);

        const expiration = theLocalStorageUtils.get(LocalStorageKeys.qsAccessTokenExpiration);
        const refreshToken = theLocalStorageUtils.get(LocalStorageKeys.qsRefreshToken);
        if (expiration && refreshToken) {
            const durationSecs = DateTimeUtils.getSecondsFromNow(expiration);
            if (durationSecs) {
                const durationMins = DateTimeUtils.getMinutesFromNow(expiration);
                //console.log(`AuthTokenHandler.start(), Found Expiration[${expiration}], Minutes[${durationMins?.minutes}]`);

                if (durationSecs.seconds < 5) {
                    // Expiration is nigh, just get a new token
                    await AuthTokenManager.getAndSave();
                } else if (durationSecs.seconds < SecondsBuffer) {
                    // Within the range of needing to refresh
                    await AuthTokenManager.refreshAndSave();
                }
            } else {
                await AuthTokenManager.getAndSave();
            }
        } else {
            await AuthTokenManager.getAndSave();
        }

        this.startTimeout();
    }

    stop(): void {
        this.timeoutWorker.stop();
    }

    /**
     * The function given to the TimeoutWorker
     */
    private refreshAndRestart = async (): Promise<void> => {
        //console.log(`AuthTokenHandler.refreshAndRestart(), Starting`);
        await AuthTokenManager.refreshAndSave().then(() => {
            this.startTimeout();
        });
    };

    private startTimeout(): void {
        const delaySeconds = this.determineDelay();
        this.timeoutWorker.setDelay(delaySeconds);
        //console.log(`AuthTokenHandler.startTimeout(), Delaying [${delaySeconds / 60}] minutes`);
    }

    private determineDelay(): number {
        const expiration = theLocalStorageUtils.get(LocalStorageKeys.qsAccessTokenExpiration);
        if (expiration) {
            const durationSecs = DateTimeUtils.getSecondsFromNow(expiration);
            if (durationSecs && durationSecs.seconds > SecondsBuffer) {
                return durationSecs.seconds - SecondsBuffer;
            } else {
                return 0;
            }
        }
        return 300 - SecondsBuffer; // default to 5 minutes minus the buffer
    }
}
